/**
 * Página de criação de novo arquivo
 * Substitui NovoArquivoModal.tsx como página dedicada
 */

import React, { useEffect, useCallback } from 'react';
import { ArrowLeft, Save, AlertCircle, FileText } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useClienteSelection, useNovoArquivoForm, useArquivoSalvamento } from './hooks';
import { ClienteSelector, CategoriaSelector, ObservacoesInput, PreviewDestino, SaveProgress } from './components';

interface NovoArquivoPageProps {
  /** Callback ao voltar para a página anterior */
  onVoltar: () => void;
  /** Callback após salvamento bem-sucedido */
  onSalvoComSucesso?: () => void;
}

export function NovoArquivoPage({ onVoltar, onSalvoComSucesso }: NovoArquivoPageProps) {
  const { userInfo } = usePermissions();
  
  // Hooks customizados
  const clienteSelection = useClienteSelection();
  const formState = useNovoArquivoForm();
  const salvamento = useArquivoSalvamento();

  // Sincronizar cliente selecionado com o formulário
  useEffect(() => {
    if (clienteSelection.cliente !== formState.form.cliente) {
      formState.setCliente(clienteSelection.cliente);
    }
  }, [clienteSelection.cliente, formState]);

  // Handler de submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar
    const validation = formState.validate(clienteSelection.digits);
    if (!validation.valid) {
      formState.setErro(validation.error || 'Preencha todos os campos obrigatórios');
      return;
    }

    // Preparar dados
    const submitData = formState.prepareSubmitData(clienteSelection.digits);

    // Salvar
    const result = await salvamento.salvar({
      submitData,
      digits: clienteSelection.digits,
      nomeOpcional: formState.form.nomeOpcional,
      userId: userInfo?.id || 0,
      userName: userInfo?.nome || 'usuario'
    });

    if (result.success) {
      // Reset e callback de sucesso
      clienteSelection.reset();
      formState.reset();
      onSalvoComSucesso?.();
    } else {
      formState.setErro(result.error || 'Erro ao salvar arquivo');
    }
  }, [
    formState,
    clienteSelection,
    salvamento,
    userInfo,
    onSalvoComSucesso
  ]);

  // Limpar erro quando o formulário for editado
  useEffect(() => {
    if (formState.erro) {
      formState.setErro(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSelection.query, formState.form.prodCatItem]);

  // Verificar se pode submeter
  const canSubmit =
    !salvamento.salvando &&
    formState.form.prodCatItem &&
    (clienteSelection.hasCadastro || clienteSelection.isWhatsValid);

  return (
    <div className="h-full flex flex-col bg-navy-900">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-navy-700">
        <button
          onClick={onVoltar}
          disabled={salvamento.salvando}
          className="p-1.5 text-gray-400 hover:text-white transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Voltar"
          aria-label="Voltar"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <h1 className="text-lg font-semibold text-white truncate">Novo Arquivo</h1>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 overflow-y-auto">
        <form id="novo-arquivo-form" onSubmit={handleSubmit} className="space-y-3">
          {/* Erro geral */}
          {formState.erro && (
            <div className="p-3 bg-red-600/10 border border-red-600/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-red-200 font-medium">Erro</p>
                <p className="text-xs text-red-100 mt-0.5">{formState.erro}</p>
              </div>
            </div>
          )}

          {/* Cliente ou WhatsApp */}
          <ClienteSelector
            query={clienteSelection.query}
            onQueryChange={clienteSelection.setQuery}
            cliente={clienteSelection.cliente}
            clientes={clienteSelection.clientes}
            carregando={clienteSelection.carregandoClientes}
            matchStatus={clienteSelection.matchStatus}
            isConfirmed={clienteSelection.isConfirmed}
            resumo={clienteSelection.resumo}
            isTypingWhatsApp={clienteSelection.isTypingWhatsApp}
            onSelectCliente={clienteSelection.selectCliente}
            onConfirmCliente={clienteSelection.confirmCliente}
            onClearCliente={clienteSelection.clearCliente}
          />

          {/* Categoria do Produto */}
          <CategoriaSelector
            categorias={formState.categorias}
            selected={formState.form.prodCatItem}
            onSelect={formState.setProdCatItem}
            loading={formState.carregandoCategorias}
          />

          {/* Observações */}
          <ObservacoesInput
            value={formState.form.observacoes}
            onChange={formState.setObservacoes}
            maxLength={500}
          />

          {/* Preview do Destino */}
          <PreviewDestino
            cliente={clienteSelection.cliente}
            isConfirmed={clienteSelection.isConfirmed}
            digits={clienteSelection.digits}
            prodCatItem={formState.form.prodCatItem}
            categorias={formState.categorias}
            userId={userInfo?.id || 0}
            userName={userInfo?.nome || 'usuario'}
          />
        </form>

        {(salvamento.salvando || !!salvamento.progress) && (
          <div className="mt-3">
            <SaveProgress stage={salvamento.progress?.stage} visible />
          </div>
        )}
      </div>

      {/* Footer com botão Salvar */}
      <div className="p-2 border-t border-navy-700 space-y-2">
        {/* Mensagem informativa quando botão está bloqueado */}
        {!canSubmit && 
         !salvamento.salvando && 
         formState.form.prodCatItem && 
         !clienteSelection.hasCadastro && 
         !clienteSelection.isWhatsValid && 
         clienteSelection.query.trim().length > 0 && (
          <div className="p-2 bg-amber-600/10 border border-amber-600/20 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-amber-200 font-medium mb-0.5">
                  Não é possível salvar o arquivo
                </p>
                <p className="text-[10px] text-amber-100 leading-relaxed">
                  Cliente não encontrado. Para salvar <strong>sem cadastro</strong>, informe um <strong>WhatsApp válido</strong> (10 ou 11 dígitos com DDD).
                </p>
              </div>
            </div>
          </div>
        )}
        
        <button
          type="submit"
          form="novo-arquivo-form"
          disabled={!canSubmit || salvamento.salvando}
          className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          title={!canSubmit ? 'Preencha todos os campos obrigatórios' : 'Salvar arquivo'}
          aria-label="Salvar arquivo"
        >
          {salvamento.salvando ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Salvando...</span>
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5" />
              <span>Salvar Arquivo</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

